/**
 * GlossarioService — Static read-only access to the contextual glossary.
 *
 * Loads the bundled JSON at construction (synchronous import via Vite),
 * exposes O(1) lookup by id and an iterator over all entries.
 *
 * Lives as its own singleton (not inside IDataService) because the glossary
 * is a UI/presentation concern: the catalog can ship without it. Future
 * locales (en, fr…) will follow the same parallel-file pattern as the
 * editorial content (ADR 0002).
 */

import type { GlossarioFile, TermineGlossario } from '@/data/glossario';
import glossarioItRaw from '@/data/glossario.it.json';
import type { Locale } from '@/i18n/locale';

const FALLBACK_LOCALE: Locale = 'it';

const GLOSSARI: Record<Locale, GlossarioFile> = {
  it: glossarioItRaw as unknown as GlossarioFile,
  // en: glossarioEnRaw as unknown as GlossarioFile,  // future
  en: {} as GlossarioFile,
};

export class GlossarioService {
  private readonly entries: Map<Locale, GlossarioFile>;

  constructor() {
    this.entries = new Map(Object.entries(GLOSSARI) as [Locale, GlossarioFile][]);
  }

  /**
   * Lookup a term by id in the requested locale.
   * Falls back to the default locale when the entry is missing in the
   * requested one (typical for the en file before translation).
   */
  getTermine(id: string, locale: Locale): TermineGlossario | null {
    const primary = this.entries.get(locale)?.[id];
    if (primary) return primary;
    if (locale !== FALLBACK_LOCALE) {
      return this.entries.get(FALLBACK_LOCALE)?.[id] ?? null;
    }
    return null;
  }

  /**
   * Returns all entries in the requested locale, sorted by id for
   * deterministic iteration (used by future GlossarioView).
   */
  getAll(locale: Locale): TermineGlossario[] {
    const file = this.entries.get(locale) ?? this.entries.get(FALLBACK_LOCALE) ?? {};
    return Object.values(file).sort((a, b) => a.id.localeCompare(b.id));
  }

  has(id: string, locale: Locale): boolean {
    return this.getTermine(id, locale) !== null;
  }
}

// ──────── Registry ────────

let instance: GlossarioService | null = null;

export function setGlossarioService(service: GlossarioService): void {
  instance = service;
}

export function getGlossarioService(): GlossarioService {
  if (!instance) {
    throw new Error(
      '[GlossarioService] getGlossarioService() called before setGlossarioService(). ' +
        'Ensure main.ts wires the service during boot.',
    );
  }
  return instance;
}
